import { useState, useEffect } from "react";
import {
  Image as ImageIcon, Save, Loader2, Check, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/api") ? `${backendUrl}${url}` : `${backendUrl}/api${url}`;
};

// Color overlay mapping (same as PageHero.jsx)
const OVERLAY_COLORS = {
  noticias: "from-blue-900/90 to-blue-800/85",
  choluteca: "from-emerald-900/90 to-emerald-800/85",
  honduras: "from-sky-900/90 to-sky-800/85",
  destinos: "from-teal-900/90 to-teal-800/85",
  experiencias: "from-orange-900/90 to-orange-800/85",
  sabores: "from-red-900/90 to-red-800/85",
  videos: "from-purple-900/90 to-purple-800/85",
  memorias: "from-amber-900/90 to-amber-800/85",
  galerias: "from-pink-900/90 to-pink-800/85",
  guias: "from-indigo-900/90 to-indigo-800/85",
  artistas: "from-fuchsia-900/90 to-fuchsia-800/85",
  directorio: "from-cyan-900/90 to-cyan-800/85",
  contacto: "from-slate-900/90 to-slate-800/85",
  compartir: "from-rose-900/90 to-rose-800/85",
  antesahora: "from-stone-900/90 to-stone-800/85",
  mapa: "from-green-900/90 to-green-800/85",
  tienda: "from-emerald-900/90 to-emerald-800/85",
  deportes: "from-lime-900/90 to-lime-800/85",
};

function SectionCard({ section, onEdit }) {
  const overlayColor = OVERLAY_COLORS[section.section_id] || "from-primary/90 to-primary/85";
  
  return (
    <div className="bg-white rounded-xl border overflow-hidden hover:shadow-md transition-shadow">
      {/* Preview */}
      <div 
        className="relative h-32 bg-cover bg-center"
        style={{ backgroundImage: `url(${getImageUrl(section.hero_image_url)})` }}
      >
        <div className={`absolute inset-0 bg-gradient-to-br ${overlayColor}`} />
        <div className="absolute inset-0 flex items-center justify-center">
          <h3 className="text-white font-bold text-xl">{section.name}</h3>
        </div>
      </div>
      
      {/* Actions */}
      <div className="p-4">
        <p className="text-xs text-gray-500 mb-3 truncate">
          {section.hero_image_url}
        </p>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full"
          onClick={() => onEdit(section)}
        >
          <ImageIcon className="h-4 w-4 mr-2" />
          Cambiar Imagen
        </Button>
      </div>
    </div>
  );
}

export default function AdminPortadas() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API}/sections/settings`);
      setSections(response.data.items || []);
    } catch (error) {
      console.error("Error fetching sections:", error);
      toast.error("Error al cargar las secciones");
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    setLoadingMedia(true);
    try {
      const response = await axios.get(`${API}/media?limit=50`);
      setMediaList(response.data.items || []);
    } catch (error) {
      console.error("Error fetching media:", error);
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    fetchMedia();
    setShowMediaPicker(true);
  };

  const handleSelectImage = async (media) => {
    if (!editingSection) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/sections/settings/${editingSection.section_id}`,
        { hero_image_url: media.url },
        
      );
      toast.success(`Imagen de "${editingSection.name}" actualizada`);
      setShowMediaPicker(false);
      setEditingSection(null);
      fetchSections();
    } catch (error) {
      toast.error("Error al actualizar la imagen");
    } finally {
      setSaving(false);
    }
  };

  const handleInitialize = async () => {
    try {
      await axios.post(`${API}/sections/settings/init`);
      toast.success("Secciones inicializadas");
      fetchSections();
    } catch (error) {
      toast.error("Error al inicializar");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-brand-text flex items-center gap-2">
              <ImageIcon className="h-6 w-6 text-primary" />
              Portadas de Secciones
            </h1>
            <p className="text-brand-textSecondary">
              Cambia la imagen de fondo de cada sección del sitio
            </p>
          </div>
          <Button variant="outline" onClick={handleInitialize}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reiniciar Todo
          </Button>
        </div>

        {/* Sections Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : sections.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <ImageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-4">No hay secciones configuradas</h3>
            <Button onClick={handleInitialize}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Inicializar Secciones
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {sections.map((section) => (
              <SectionCard 
                key={section.section_id} 
                section={section} 
                onEdit={handleEdit}
              />
            ))}
          </div>
        )}

        {/* Media Picker Dialog */}
        <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Seleccionar imagen para "{editingSection?.name}"
              </DialogTitle>
            </DialogHeader>
            
            {loadingMedia ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  Selecciona una imagen de la biblioteca de medios. 
                  Puedes subir nuevas imágenes desde la sección "Media".
                </p>
                
                {mediaList.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No hay imágenes disponibles. Sube imágenes desde la sección "Media".
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {mediaList.map((media) => (
                      <div
                        key={media.media_id}
                        className={`aspect-video rounded-lg overflow-hidden cursor-pointer border-2 transition-all hover:ring-2 hover:ring-primary ${
                          editingSection?.hero_image_url === media.url 
                            ? "border-primary ring-2 ring-primary" 
                            : "border-transparent"
                        }`}
                        onClick={() => handleSelectImage(media)}
                      >
                        <img
                          src={getImageUrl(media.url)}
                          alt={media.title || "Media"}
                          className="w-full h-full object-cover"
                        />
                        {editingSection?.hero_image_url === media.url && (
                          <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                            <Check className="h-8 w-8 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {saving && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
                    Guardando...
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
